import React, { useReducer, useState } from 'react';

// Hooks
import useUIPersistance from '../hooks/useUIPersistance';

// Interfaces
import { FiltersPatients, FiltersPolls } from '../interfaces/Filters';
import { TerminalPhase } from '../interfaces/Phase';
import { Profile } from '../interfaces/Profile';
import { ProfileAndZones } from '../interfaces/Department';

type PollFiltersDispatch =
    { type: 'profileIDs', payload: number[] } |
    { type: 'frequencyIDs', payload: number[] } |
    { type: 'showTablesIDs', payload: number[] } |
    { type: 'typeIDs', payload: number[] } |
    { type: 'scoreAlertsIDs', payload: number[] } |
    { type: 'SET_DEFAULT', profileIDs: number[], frequencyIDs: number[], showTablesIDs: number[], typeIDs: number[], scoreAlertsIDs: number[] } |
    { type: 'DELETE_FILTERS' };

type PatientFiltersDispatch =
    { type: 'PROFILE', payload: number[] } |
    { type: 'DAYS_ENTRY', payload: { min: number, max: number | null } } |
    { type: 'SEARCHBAR', text: string } |
    { type: 'SET_DEFAULT_FILTERS', profileIDs: number[], daysEntryRange: number[] } |
    { type: 'DELETE_FILTERS' } |
    { type: 'DELETE' };

interface FiltersContextInterface {
    profiles: Profile[];
    setProfiles: React.Dispatch<React.SetStateAction<Profile[]>>;
    profilesZones: ProfileAndZones[];
    setProfilesZones: React.Dispatch<React.SetStateAction<ProfileAndZones[]>>;
    currentProfileID: number | null;
    setCurrentProfileID: React.Dispatch<React.SetStateAction<number | null>>;
    terminalPhases: TerminalPhase[];
    setTerminalPhases: React.Dispatch<React.SetStateAction<TerminalPhase[]>>;
    patientFilters: FiltersPatients;
    dispatchPatientsFilters: React.Dispatch<PatientFiltersDispatch>;
    pollsFilters: FiltersPolls;
    dispatchPollsFilters: React.Dispatch<PollFiltersDispatch>;
}

export const initFiltersContext: FiltersContextInterface = {
    profiles: [],
    setProfiles: () => undefined,
    profilesZones: [],
    setProfilesZones: () => undefined,
    currentProfileID: null,
    setCurrentProfileID: () => undefined,
    terminalPhases: [],
    setTerminalPhases: () => undefined,
    patientFilters: {
        currentProfileID: null,
        daysEntryRange: { min: 0, max: null },
        currentSearchbarText: ''
    },
    dispatchPatientsFilters: () => undefined,
    pollsFilters: {
        profileIDs: [],
        frequencyIDs: [],
        showTablesIDs: [],
        typeIDs: [],
        scoreAlertsIDs: []
    },
    dispatchPollsFilters: () => undefined,
};

const FiltersContext = React.createContext<FiltersContextInterface>(initFiltersContext);

const initPatientFilters: FiltersPatients = {
    currentProfileID: null,
    daysEntryRange: { min: 0, max: null },
    currentSearchbarText: '',
};

const initPollFilters: FiltersPolls = {
    profileIDs: [],
    frequencyIDs: [],
    showTablesIDs: [],
    typeIDs: [],
    scoreAlertsIDs: []
};

export const FiltersContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { set } = useUIPersistance();

    const pollsFiltersReducer = (
        state: FiltersPolls,
        action: PollFiltersDispatch
    ): FiltersPolls => {
        const setUserUIPersitence = (
            type: 'profileIDs' | 'frequencyIDs' | 'showTablesIDs' | 'typeIDs' | 'scoreAlertsIDs',
            value: string
        ) => set(`bchome_filter_polls_${type}`, value);

        if (
            action.type === 'profileIDs' ||
            action.type === 'frequencyIDs' ||
            action.type === 'showTablesIDs' ||
            action.type === 'typeIDs' ||
            action.type === 'scoreAlertsIDs'
        ) {
            setUserUIPersitence(action.type, action.payload.toString());
            return {
                ...state,
                [action.type]: action.payload
            };
        }

        if (action.type === 'SET_DEFAULT') {
            const { profileIDs, frequencyIDs, showTablesIDs, typeIDs, scoreAlertsIDs } = action;
            return {
                ...state,
                profileIDs,
                frequencyIDs,
                showTablesIDs,
                typeIDs,
                scoreAlertsIDs
            };
        }

        if (action.type === 'DELETE_FILTERS') {
            setUserUIPersitence('profileIDs', '');
            setUserUIPersitence('frequencyIDs', '');
            setUserUIPersitence('showTablesIDs', '');
            setUserUIPersitence('typeIDs', '');
            setUserUIPersitence('scoreAlertsIDs', '');
            return initPollFilters;
        }

        return state;
    };

    const patientsFiltersReducer = (
        state: FiltersPatients,
        action: PatientFiltersDispatch
    ): FiltersPatients => {
        const setUserUIPersitence = (type: 'profileIDs' | 'daysEntryRange', value: string) =>
            set(`bchome_filter_patient_${type}`, value);

        if (action.type === 'PROFILE') {
            setUserUIPersitence('profileIDs', action.payload[0]?.toString() ?? '');
            return {
                ...state,
                currentProfileID: action.payload[0] ?? null
            };
        }

        if (action.type === 'DAYS_ENTRY') {
            setUserUIPersitence(
                'daysEntryRange',
                action.payload.min.toString() + ',' + (action.payload.max?.toString() ?? '')
            );
            return {
                ...state,
                daysEntryRange: action.payload
            };
        }

        if (action.type === 'SEARCHBAR') {
            return {
                ...state,
                currentSearchbarText: action.text
            };
        }

        if (action.type === 'SET_DEFAULT_FILTERS') {
            const { profileIDs, daysEntryRange } = action;
            return {
                ...state,
                ...(profileIDs[0] === undefined ? {} : { currentProfileID: profileIDs[0] }),
                daysEntryRange: {
                    min: daysEntryRange[0] ?? 0,
                    max: daysEntryRange[1] === 0 || daysEntryRange[1] === undefined ? null : daysEntryRange[1]
                }
            };
        }

        if (action.type === 'DELETE_FILTERS') {
            setUserUIPersitence('profileIDs', '');
            setUserUIPersitence('daysEntryRange', '');
            return {
                ...initPatientFilters,
                currentProfileID: state.currentProfileID,
                currentSearchbarText: state.currentSearchbarText
            };
        }

        if (action.type === 'DELETE') {
            return initPatientFilters;
        }

        return state;
    };

    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [profilesZones, setProfilesZones] = useState<ProfileAndZones[]>([]);
    const [currentProfileID, setCurrentProfileID] = useState<number | null>(null);
    const [terminalPhases, setTerminalPhases] = useState<TerminalPhase[]>([]);

    const [patientFilters, dispatchPatientsFilters] =
        useReducer(patientsFiltersReducer, initPatientFilters);

    const [pollsFilters, dispatchPollsFilters] =
        useReducer(pollsFiltersReducer, initPollFilters);

    const initState: FiltersContextInterface = {
        profiles,
        setProfiles,
        profilesZones,
        setProfilesZones,
        currentProfileID,
        setCurrentProfileID,
        terminalPhases,
        setTerminalPhases,
        patientFilters,
        dispatchPatientsFilters,
        pollsFilters,
        dispatchPollsFilters
    };

    return (
        <FiltersContext.Provider value={initState}>
            {children}
        </FiltersContext.Provider>
    );
};

export default FiltersContext;